import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import activityTracker from '../../utils/activityTracker';

/**
 * InactivityMonitor component
 * 
 * This component monitors user activity and shows a warning when the user is about to be
 * logged out due to inactivity. It also handles the auto-logout functionality.
 */
const InactivityMonitor = () => {
  const { user, logout } = useContext(AuthContext);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [countdownInterval, setCountdownInterval] = useState(null);
  
  // Handle warning display
  const handleInactivityWarning = () => {
    setShowWarning(true);
    
    // Start countdown
    let secondsLeft = 60;
    setCountdown(secondsLeft);
    
    const interval = setInterval(() => {
      secondsLeft -= 1;
      setCountdown(secondsLeft);
      
      if (secondsLeft <= 0) {
        clearInterval(interval);
      }
    }, 1000);
    
    setCountdownInterval(interval);
  };
  
  // Handle logout due to inactivity
  const handleInactivityLogout = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
    
    setShowWarning(false);
    logout();
    
    // Redirect to login page
    window.location.href = '/auth/employer';
  };
  
  // Continue session
  const continueSession = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
    
    setShowWarning(false);
    
    // Reset the activity tracker
    activityTracker.resetTimer();
  };
  
  // Set up activity tracking when component mounts
  useEffect(() => {
    if (user) {
      // DISABLE auto-logout for employers - only track candidates and recruiters
      const isEmployer = user.userType === 'employer';
      
      if (isEmployer) {
        console.log('[InactivityMonitor] Auto-logout disabled for employers');
        return; // Don't track employers
      }
      
      // Check if user is a recruiter
      const isRecruiter = user.userType === 'recruiter';
      
      // Start tracking activity with user ID (only for candidates and recruiters)
      activityTracker.startTracking(
        isRecruiter,
        handleInactivityWarning,
        handleInactivityLogout,
        user.id // Pass the user ID for tracking login sessions
      );
    }
    
    // Clean up when component unmounts
    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
      activityTracker.stopTracking();
    };
  }, [user]);
  
  // Don't render anything if warning is not shown
  if (!showWarning) {
    return null;
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg">
        <h3 className="text-xl font-bold text-black mb-4">Session Timeout Warning</h3>
        <p className="mb-6 text-gray-700">
          Your session is about to expire due to inactivity. You will be logged out in <span className="font-bold">{countdown}</span> seconds.
        </p>
        <div className="flex justify-end">
          <button
            onClick={continueSession}
            className="px-6 py-2 bg-black text-white font-medium hover:bg-gray-800 transition-colors"
          >
            Continue Session
          </button>
        </div>
      </div>
    </div>
  );
};

export default InactivityMonitor;
